import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import Swal from "sweetalert2";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import "./reportpanel.css";
import MainContent from "../../components/MainContent/MainContent";
import AppShell from "../AppShell/AppShell";
import { DashboardMetricsAPI } from "../../api/DashboardMetrics";
import { ReportTemplatesAPI } from "../../api/ReportTemplates";
import GuidedTour from "./GuidedTour/GuidedTour";
import ChooserView from "./Legacy/ChooserView";
import SmartpayCatalogView from "./Legacy/SmartpayCatalogView";
import LegacyPreviewView from "./Legacy/LegacyPreviewView";
import { isLegacyTemplate, normalizeLegacyTemplate } from "./Legacy/legacyTemplates";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const uid = () => Math.random().toString(36).substr(2, 9);
const fmt = (n) => new Intl.NumberFormat("es-PA", { style: "currency", currency: "USD" }).format(n);
const fmtMetric = (n) => new Intl.NumberFormat("es-PA", { maximumFractionDigits: 2 }).format(Number(n) || 0);
const toISO = () => new Date().toISOString().split("T")[0];
const ago = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split("T")[0]; };
const fmtD = (iso) => { if (!iso) return ""; const p = iso.split("-"); return `${p[2]}/${p[1]}/${p[0]}`; };
const PALETTE = ["#5B5BD6", "#1B8C1B", "#D97706", "#0891B2", "#C53030", "#7C3AED", "#059669", "#DC2626", "#2563EB", "#CA8A04"];

/* ═══ ICONS ══════════════════════════════════════════════════ */
const I = {
  Plus: ({ s = 16 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
  X: ({ s = 16 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
  ArrowLeft: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>,
  Search: ({ s = 15 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>,
  Trash: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>,
  Edit: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" /></svg>,
  Copy: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>,
  Play: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3" /></svg>,
  Save: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>,
  Eye: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>,
  BarC: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="12" width="4" height="9" rx="1" /><rect x="10" y="5" width="4" height="16" rx="1" /><rect x="17" y="8" width="4" height="13" rx="1" /></svg>,
  LineC: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><polyline points="22 12 18 8 13 13 9 9 2 16" /></svg>,
  PieC: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M21.21 15.89A10 10 0 1 1 8 2.83" /><path d="M22 12A10 10 0 0 0 12 2v10z" /></svg>,
  AreaC: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M2 20l5-8 4 4 5-6 6 3v7H2z" /></svg>,
  TableC: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" /><line x1="9" y1="3" x2="9" y2="21" /></svg>,
  KpiC: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="2" y="7" width="6" height="10" rx="1" /><rect x="9" y="3" width="6" height="14" rx="1" /><rect x="16" y="10" width="6" height="7" rx="1" /><line x1="2" y1="21" x2="22" y2="21" /></svg>,
  Layout: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="9" x2="9" y2="21" /></svg>,
  ChevDown: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9" /></svg>,
  ChevLeft: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6" /></svg>,
  ChevRight: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6" /></svg>,
  Grip: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="9" cy="5" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="19" r="1" /><circle cx="15" cy="5" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="19" r="1" /></svg>,
  Download: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>,
  Sparkle: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3z" /></svg>,
  Clock: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
  Calendar: () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>,
  Filter: () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>,
  Refresh: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></svg>,
  SortAsc: () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="18 15 12 9 6 15" /></svg>,
  SortDesc: () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9" /></svg>,
};

const QUICK = [
  { label: "Hoy", from: toISO, to: toISO },
  { label: "7 días", from: () => ago(7), to: toISO },
  { label: "15 días", from: () => ago(15), to: toISO },
  { label: "30 días", from: () => ago(30), to: toISO },
  { label: "90 días", from: () => ago(90), to: toISO },
];

/* ═══ DATA ═══════════════════════════════════════════════════ */
const CHARTS = [{ id: "bar", label: "Barras", icon: <I.BarC /> }, { id: "bar_h", label: "Barras H", icon: <I.BarC /> }, { id: "line", label: "Líneas", icon: <I.LineC /> }, { id: "area", label: "Área", icon: <I.AreaC /> }, { id: "pie", label: "Circular", icon: <I.PieC /> }, { id: "table", label: "Tabla", icon: <I.TableC /> }, { id: "kpi", label: "KPI", icon: <I.KpiC /> }, { id: "stat_delta", label: "KPI", icon: <I.KpiC /> }];

const CHART_BLOCKS = ["bar", "bar_h", "line", "area", "pie"];
const NUMERIC_TYPES = ["number", "currency", "percentage", "decimal", "integer", "float"];

const asArray = (value) => Array.isArray(value) ? value : value ? [value] : [];
const uniq = (arr) => [...new Set(arr.filter(Boolean))];
const humanize = (value = "") => String(value).replace(/[_-]+/g, " ").replace(/\b\w/g, c => c.toUpperCase());
const getToken = () => localStorage.getItem("x-access-token") || "";
const getStoredBusinessCode = () => localStorage.getItem("business_code") || localStorage.getItem("businessCode") || localStorage.getItem("business") || "";
const getStorageJSON = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};
const getUserBranches = () => {
  const user = getStorageJSON("user");
  return (Array.isArray(user?.branchoffices) ? user.branchoffices : [])
    .map((branch) => {
      const code = branch.code || branch.branchoffice_code || branch.value;
      if (!code) return null;
      return {
        code: String(code),
        name: branch.branchoffice_name || branch.name || branch.label || String(code),
      };
    })
    .filter(Boolean);
};
const branchSummary = (branches, selectedCodes) => {
  if (!branches.length) return "Sin sucursales";
  if (!selectedCodes.length || selectedCodes.length === branches.length) return "Todas";
  if (selectedCodes.length === 1) return branches.find(branch => branch.code === selectedCodes[0])?.name || "1 sucursal";
  return `${selectedCodes.length} sucursales`;
};
const chartTypeOf = (block) => block?.chartType || block?.type || "";
const chartMeta = (chartType) => CHARTS.find(c => c.id === chartType) || { id: chartType, label: humanize(chartType || "Bloque"), icon: <I.Layout /> };
const isMetricChart = (chartType) => CHART_BLOCKS.includes(chartType);
const isNumericField = (field) => NUMERIC_TYPES.includes(String(field?.type || "").toLowerCase());
const unwrapCatalog = (response) => response?.data?.data?.catalog || response?.data?.catalog || response?.data?.data || response?.data || response;
const unwrapMetricData = (response) => response?.data?.data || response?.data?.results || response?.data || response;

function normalizeList(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(v => typeof v === "string" ? v : v?.id || v?.key || v?.value || v?.type).filter(Boolean);
  if (typeof value === "string") return value.split(",").map(v => v.trim()).filter(Boolean);
  return [];
}

function normalizeField(field, fallbackId) {
  const raw = typeof field === "string" ? { id: field } : field || {};
  const id = String(raw.id || raw.key || raw.name || raw.field || raw.metricId || fallbackId || "");
  return {
    id,
    key: id,
    label: raw.label || raw.title || raw.name || humanize(id),
    type: String(raw.type || raw.data_type || raw.kind || "text").toLowerCase(),
    role: String(raw.role || raw.group || "").toLowerCase(),
    chartTypes: normalizeList(raw.chartTypes || raw.chart_types || raw.allowedChartTypes || raw.allowed_chart_types),
    groupByOptions: normalizeList(raw.groupByOptions || raw.group_by_options || raw.group_by || raw.groupBy),
  };
}

function normalizeFieldCollection(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((field, index) => normalizeField(field, `field_${index}`)).filter(f => f.id);
  return Object.entries(value).map(([id, field]) => normalizeField({ id, ...(typeof field === "object" ? field : {}) }, id)).filter(f => f.id);
}

function resolveFieldRefs(refs, allFields, fallback) {
  const source = refs ? asArray(refs) : [];
  if (source.length) {
    return source.map((ref, index) => {
      const id = typeof ref === "string" ? ref : ref?.id || ref?.key || ref?.field || ref?.metricId || `field_${index}`;
      return allFields.find(f => f.id === id) || normalizeField(ref, id);
    }).filter(f => f.id);
  }
  return allFields.filter(fallback);
}

function normalizeFilters(filters) {
  return asArray(filters).map((filter) => {
    const key = filter.key || filter.id || filter.field || filter.name;
    if (!key) return null;
    return {
      key,
      label: filter.label || filter.title || humanize(key),
      options: normalizeList(filter.options || filter.values || filter.allowed_values),
    };
  }).filter(Boolean);
}

function normalizeCatalog(payload) {
  const catalog = unwrapCatalog(payload);
  const rawSources = catalog?.sources || catalog?.data_sources || catalog?.items || catalog || [];
  const sourceList = Array.isArray(rawSources)
    ? rawSources
    : Object.entries(rawSources).map(([id, value]) => ({ id, ...(typeof value === "object" ? value : {}) }));

  return sourceList.map((source, index) => {
    const id = String(source.id || source.key || source.source || source.name || `source_${index}`);
    const fields = normalizeFieldCollection(source.fields || source.available_fields || source.columns || source.metrics);
    const xFields = resolveFieldRefs(source.x_fields || source.xFields || source.dimensions, fields, f => !isNumericField(f));
    const yFields = resolveFieldRefs(source.y_fields || source.yFields || source.metrics, fields, isNumericField);
    const tableFields = resolveFieldRefs(source.table_fields || source.tableFields || source.columns, fields, () => true);
    const chartTypes = uniq([
      ...normalizeList(source.chartTypes || source.chart_types || source.allowedChartTypes || source.allowed_chart_types),
      ...fields.flatMap(f => f.chartTypes),
    ]);
    const groupByOptions = uniq([
      ...normalizeList(source.groupByOptions || source.group_by_options || source.group_by || source.groupBy),
      ...fields.flatMap(f => f.groupByOptions),
    ]);

    return {
      id,
      label: source.label || source.title || humanize(id),
      icon: source.icon || "",
      color: source.color || PALETTE[index % PALETTE.length],
      fields,
      xFields: xFields.length ? xFields : fields,
      yFields: yFields.length ? yFields : fields.filter(isNumericField),
      tableFields: tableFields.length ? tableFields : fields,
      chartTypes: chartTypes.length ? chartTypes : CHARTS.map(c => c.id),
      groupByOptions,
      filters: normalizeFilters(source.filters),
    };
  }).filter(source => source.id);
}

function fieldAllowsChart(field, chartType) {
  return !field?.chartTypes?.length || field.chartTypes.includes(chartType);
}

function getAllowedCharts(source) {
  if (!source) return [];
  return source.chartTypes.filter(type => CHARTS.some(c => c.id === type)).map(type => chartMeta(type));
}

function getFieldsFor(source, chartType, role) {
  if (!source) return [];
  const fields = role === "x" ? source.xFields : role === "table" ? source.tableFields : source.yFields;
  const filtered = fields.filter(field => fieldAllowsChart(field, chartType));
  return filtered.length ? filtered : fields;
}

function blockConfig(block) {
  return block?.config || {};
}

function normalizeBlock(block, source) {
  const chartType = chartTypeOf(block) || "bar";
  const config = blockConfig(block);
  const columns = config.columns || block.fields || [];
  const yField = config.y_field || block.fieldY || block.field || block.metricId || "";
  const nextConfig = {
    source: config.source || source?.id || block.source || "",
    compare_with_previous_period: Boolean(config.compare_with_previous_period),
  };

  if (isMetricChart(chartType)) {
    nextConfig.x_field = config.x_field || block.fieldX || "";
    nextConfig.y_field = yField;
    if (config.group_by) nextConfig.group_by = config.group_by;
  } else if (chartType === "kpi" || chartType === "stat_delta") {
    nextConfig.y_field = yField;
  } else if (chartType === "table") {
    nextConfig.columns = columns;
  }

  return {
    ...block,
    id: block.id || uid(),
    chartType,
    metricId: block.metricId || yField || columns[0] || chartType,
    title: block.title || chartMeta(chartType).label,
    size: block.size || (chartType === "kpi" || chartType === "stat_delta" ? "third" : "full"),
    config: nextConfig,
  };
}

function createBlock(source, chartType) {
  const xField = getFieldsFor(source, chartType, "x")[0]?.id || "";
  const yField = getFieldsFor(source, chartType, "y")[0]?.id || "";
  const columns = getFieldsFor(source, chartType, "table").slice(0, 4).map(f => f.id);
  const config = { source: source.id, compare_with_previous_period: false };
  if (isMetricChart(chartType)) {
    config.x_field = xField;
    config.y_field = yField;
    if (source.groupByOptions[0]) config.group_by = source.groupByOptions[0];
  } else if (chartType === "kpi" || chartType === "stat_delta") {
    config.y_field = yField;
  } else if (chartType === "table") {
    config.columns = columns;
  }
  return normalizeBlock({ id: uid(), chartType, metricId: yField || columns[0] || chartType, title: chartMeta(chartType).label, size: chartType === "kpi" || chartType === "stat_delta" ? "third" : "full", config }, source);
}

function toMetricWidget(block, index) {
  const clean = normalizeBlock(block);
  return {
    layout_id: clean.id,
    slot_index: index,
    metricId: clean.metricId,
    chartType: clean.chartType,
    title: clean.title,
    config: clean.config,
  };
}

function extractRows(result) {
  const data = result?.data ?? result?.rows ?? result?.items ?? result;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (data && typeof data === "object") return [data];
  return [];
}

function resultHasData(result) {
  if (!result) return false;
  const rows = extractRows(result);
  if (rows.length) return rows.some(row => row && Object.keys(row).length > 0);
  return result.value !== undefined && result.value !== null && Number(result.value) !== 0;
}

function normalizeResolveResults(response, blocks) {
  const payload = unwrapMetricData(response);
  const list = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : payload ? [payload] : [];
  return blocks.reduce((acc, block, index) => {
    const match = list.find(item => item?.layout_id === block.id || item?.slot_index === index) || list[index];
    const error = match?.error || (match?.success === false ? match?.message : "");
    acc[block.id] = { loading: false, error, payload: match || null, noData: !error && !resultHasData(match) };
    return acc;
  }, {});
}

function unwrapTemplates(response) {
  const payload = response?.data?.data || response?.data?.templates || response?.data || response;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.templates)) return payload.templates;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function getTemplateBackendId(template) {
  if (!template || template.isNew) return null;
  return template._id || template.id || null;
}

function normalizeTemplate(template) {
  const id = template.id || template._id || uid();
  return {
    ...template,
    id,
    _id: template._id || template.id,
    name: template.name || "Sin nombre",
    description: template.description || "",
    source: template.source || template.data_source || "",
    updatedAt: template.updatedAt || template.updated_at || template.updated_at_date || toISO(),
    blocks: (template.blocks || []).map(block => normalizeBlock(block)),
  };
}

function templatePayload(template, businessCode, source) {
  const isLegacy = template.engine === "LEGACY";
  const payload = {
    name: template.name,
    description: template.description || "",
    source: isLegacy ? null : template.source,
    updatedAt: toISO(),
    origin: template.origin || "USER",
    engine: template.engine || "METRICS",
    legacy_report_code: template.legacy_report_code || null,
    // Una plantilla SMARTPAY/LEGACY no tiene bloques (no editable como METRICS) - conserva
    // solo sus filtros guardados; para METRICS el comportamiento es exactamente el mismo de antes.
    blocks: isLegacy ? [] : (template.blocks || []).map(block => {
      const clean = normalizeBlock(block, source);
      return {
        metricId: clean.metricId,
        chartType: clean.chartType,
        title: clean.title,
        size: clean.size,
        config: clean.config,
      };
    }),
    filters: isLegacy ? (template.filters || {}) : undefined,
  };
  if (businessCode) payload.business_code = businessCode;
  return payload;
}

function getErrorMessage(error, fallback) {
  return error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback;
}

function showExportToast(message, icon = "error") {
  Swal.fire({
    toast: true,
    position: "top-end",
    icon,
    title: message,
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
  });
}

function safeFileName(value) {
  return String(value || "reporte")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w.-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase() || "reporte";
}

function getFilenameFromDisposition(disposition) {
  if (!disposition) return "";
  const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) return decodeURIComponent(utfMatch[1]);
  const match = disposition.match(/filename="?([^"]+)"?/i);
  return match?.[1] || "";
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function selectedBranchNames(branches, codes) {
  if (!branches.length) return "Todas";
  if (!codes.length || codes.length === branches.length) return "Todas";
  return codes.map(code => branches.find(branch => branch.code === code)?.name || code).join(", ");
}

function getExportTables(blocks, metricData, source) {
  return blocks
    .filter(block => chartTypeOf(block) === "table")
    .map((block) => {
      const config = blockConfig(block);
      const fields = (config.columns || []).map(fid => getField(source, fid)).filter(Boolean);
      const rows = extractRows(metricData[block.id]?.payload);
      return {
        id: block.id,
        title: block.title || "Tabla",
        fields,
        rows,
        columns: fields.map(field => ({ key: field.id, label: field.label, type: field.type })),
        data: rows.map(row => fields.reduce((acc, field) => ({ ...acc, [field.id]: row[field.id] ?? row[field.key] ?? "" }), {})),
      };
    })
    .filter(table => table.fields.length && table.rows.length);
}

function buildExportSummary(blocks, metricData) {
  return blocks.map((block) => {
    const payload = metricData[block.id]?.payload;
    const rows = extractRows(payload);
    const value = payload?.formatted_value ?? payload?.value ?? (rows.length ? `${rows.length} filas` : "Sin informacion");
    return [block.title || chartMeta(chartTypeOf(block)).label, chartMeta(chartTypeOf(block)).label, String(value)];
  });
}

function buildReportExportPayload({ template, businessCode, blocks, filters, branchCodes, branches, metricData, source }) {
  const tableData = getExportTables(blocks, metricData, source).map(table => ({
    title: table.title,
    columns: table.columns,
    rows: table.data,
  }));

  return {
    business_code: businessCode,
    report_key: template._id || template.id || safeFileName(template.name),
    filters: { ...filters, branchoffice_code: branchCodes },
    branchoffice_code: branchCodes,
    blocks: blocks.map(toMetricWidget),
    tableData,
    metadata: {
      title: template.name || "Reporte",
      source: template.source,
      branch_label: selectedBranchNames(branches, branchCodes),
      generated_at: new Date().toISOString(),
    },
  };
}

function tableTotals(table) {
  return table.fields
    .filter(isNumericField)
    .map(field => ({
      label: field.label,
      value: table.rows.reduce((sum, row) => sum + (Number(row[field.id] ?? row[field.key]) || 0), 0),
      field,
    }));
}

function buildReportPdfDefinition({ template, filters, branchLabel, tables, summaryRows }) {
  const generatedAt = new Intl.DateTimeFormat("es-PA", { dateStyle: "short", timeStyle: "short" }).format(new Date());
  const filterRows = [
    ["Periodo", filters.date_range === "custom" ? `${filters.date_from || "Inicio"} - ${filters.date_to || "Hoy"}` : humanize(filters.date_range || "month")],
    ["Sucursal", branchLabel],
    ...Object.entries(filters)
      .filter(([key]) => !["date_range", "date_from", "date_to", "branchoffice_code"].includes(key))
      .map(([key, value]) => [humanize(key), String(value)]),
  ];

  const content = [
    { text: template.name || "Reporte personalizado", style: "header" },
    { text: `Generado: ${generatedAt}`, style: "muted", margin: [0, 0, 0, 12] },
    { text: "Filtros", style: "section" },
    {
      table: { widths: ["35%", "65%"], body: filterRows },
      layout: "lightHorizontalLines",
      margin: [0, 0, 0, 14],
    },
    { text: "Resumen", style: "section" },
    {
      table: {
        headerRows: 1,
        widths: ["*", "30%", "25%"],
        body: [["Bloque", "Tipo", "Resultado"], ...summaryRows],
      },
      layout: "lightHorizontalLines",
      margin: [0, 0, 0, 14],
    },
  ];

  if (!tables.length) {
    content.push({ text: "No hay un bloque de tabla configurado para exportar en PDF.", style: "empty" });
  }

  tables.forEach((table) => {
    const body = [
      table.fields.map(field => field.label),
      ...table.rows.map(row => table.fields.map(field => formatFieldValue(row[field.id] ?? row[field.key], field))),
    ];
    const totals = tableTotals(table);
    content.push({ text: table.title, style: "section", margin: [0, 8, 0, 6] });
    content.push({
      table: { headerRows: 1, widths: table.fields.map(() => "*"), body },
      layout: "lightHorizontalLines",
      margin: [0, 0, 0, totals.length ? 6 : 12],
    });
    if (totals.length) {
      content.push({
        text: totals.map(total => `${total.label}: ${formatFieldValue(total.value, total.field)}`).join("    "),
        style: "muted",
        margin: [0, 0, 0, 12],
      });
    }
  });

  return {
    pageOrientation: "landscape",
    pageMargins: [28, 28, 28, 28],
    content,
    defaultStyle: { fontSize: 9 },
    styles: {
      header: { fontSize: 18, bold: true, color: "#1A1A2E", margin: [0, 0, 0, 4] },
      section: { fontSize: 12, bold: true, color: "#1A1A2E", margin: [0, 0, 0, 6] },
      muted: { fontSize: 9, color: "#6B6B80" },
      empty: { fontSize: 10, color: "#6B6B80", italics: true, margin: [0, 0, 0, 12] },
    },
  };
}

// Utilidades compartidas con los componentes de Plantillas SmartPay (carpeta ./Legacy) -
// evita duplicar iconos/helpers ya existentes en este archivo.
export {
  I, QUICK, uid, toISO, ago, fmtD, getToken, getStoredBusinessCode, getUserBranches,
  branchSummary, getErrorMessage, showExportToast, safeFileName, getFilenameFromDisposition,
  downloadBlob, humanize,
};

export default function ReportPlanel() {
  const { business_code } = useParams();
  const [view, setView] = useState("list");
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState("");
  const [catalogSources, setCatalogSources] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState("");
  const [smartpayCatalog, setSmartpayCatalog] = useState([]);
  const [smartpayCatalogLoading, setSmartpayCatalogLoading] = useState(true);
  const [smartpayCatalogError, setSmartpayCatalogError] = useState("");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [tourSignal, setTourSignal] = useState(0);
  const tourActionsRef = useRef({});
  const tourOpenedBuilderRef = useRef(false);
  const businessCode = business_code || getStoredBusinessCode();
  const filtered = templates.filter(t => t.name.toLowerCase().includes(search.toLowerCase()));

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setTemplatesError("");
    try {
      const response = await ReportTemplatesAPI.getTemplates(getToken(), businessCode);
      setTemplates(unwrapTemplates(response).map(normalizeTemplate));
    } catch (error) {
      setTemplates([]);
      setTemplatesError(getErrorMessage(error, "No se pudieron cargar las plantillas"));
    } finally {
      setTemplatesLoading(false);
    }
  }, [businessCode]);

  useEffect(() => {
    let alive = true;
    const loadCatalog = async () => {
      setCatalogLoading(true);
      setCatalogError("");
      try {
        const response = await DashboardMetricsAPI.getCatalog(getToken());
        if (!alive) return;
        setCatalogSources(normalizeCatalog(response));
      } catch (error) {
        if (!alive) return;
        setCatalogError(error?.response?.data?.message || error?.message || "No se pudo cargar el catalogo");
        setCatalogSources([]);
      } finally {
        if (alive) setCatalogLoading(false);
      }
    };
    loadCatalog();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    const loadSmartpayCatalog = async () => {
      setSmartpayCatalogLoading(true);
      setSmartpayCatalogError("");
      try {
        const response = await ReportTemplatesAPI.getSmartpayCatalog(getToken());
        if (!alive) return;
        const payload = response?.data;
        setSmartpayCatalog(Array.isArray(payload) ? payload : (payload?.data || []));
      } catch (error) {
        if (!alive) return;
        setSmartpayCatalogError(getErrorMessage(error, "No se pudo cargar el catálogo de Plantillas SmartPay"));
        setSmartpayCatalog([]);
      } finally {
        if (alive) setSmartpayCatalogLoading(false);
      }
    };
    loadSmartpayCatalog();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const startNew = () => { setView("choose"); };
  const startFromScratch = () => { setEditing({ id: uid(), isNew: true, name: "", description: "", source: "", updatedAt: toISO(), blocks: [] }); setView("builder"); };
  const edit = (t) => {
    if (isLegacyTemplate(t)) { preview(t); return; } // las plantillas SmartPay/LEGACY no son editables como METRICS
    const src = catalogSources.find(s => s.id === t.source);
    setEditing({ ...t, blocks: t.blocks.map(b => normalizeBlock(b, src)) });
    setView("builder");
  };
  const onSmartpayTemplateCreated = async (created) => {
    await loadTemplates();
    preview(normalizeLegacyTemplate(created || {}));
  };
  const dup = async (t) => {
    const src = catalogSources.find(s => s.id === t.source);
    try {
      setTemplatesError("");
      await ReportTemplatesAPI.createTemplate(templatePayload({ ...t, name: `${t.name} (copia)`, blocks: t.blocks.map(b => ({ ...b, id: uid() })) }, businessCode, src), getToken());
      await loadTemplates();
    } catch (error) {
      setTemplatesError(getErrorMessage(error, "No se pudo duplicar la plantilla"));
    }
  };
  const del = async (id) => {
    const template = templates.find(t => t.id === id || t._id === id);
    const backendId = getTemplateBackendId(template);
    if (!backendId) return;
    try {
      setTemplatesError("");
      await ReportTemplatesAPI.deleteTemplate(backendId, getToken());
      if (previewing?.id === id || previewing?._id === id) setPreviewing(null);
      await loadTemplates();
    } catch (error) {
      setTemplatesError(getErrorMessage(error, "No se pudo eliminar la plantilla"));
    }
  };
  const save = async (t) => {
    const src = catalogSources.find(s => s.id === t.source);
    const payload = templatePayload(t, businessCode, src);
    const backendId = getTemplateBackendId(t);
    try {
      setTemplatesError("");
      if (backendId) await ReportTemplatesAPI.updateTemplate(backendId, payload, getToken());
      else await ReportTemplatesAPI.createTemplate(payload, getToken());
      await loadTemplates();
      setView("list");
      setEditing(null);
    } catch (error) {
      setTemplatesError(getErrorMessage(error, "No se pudo guardar la plantilla"));
    }
  };
  const preview = (t) => { setPreviewing(t); setView("preview"); };

  // ── Acciones que el tour dispara al entrar a ciertos pasos ──
  // Abrir el builder editando la primera plantilla (solo si hay plantillas).
  tourActionsRef.current.openBuilderForTour = () => {
    // El recorrido guiado solo sabe abrir el constructor de plantillas METRICS - si la
    // primera plantilla es SmartPay/LEGACY (no editable como METRICS), busca la primera
    // plantilla METRICS disponible en su lugar; si no hay ninguna, se salta este paso.
    const target = templates.find(t => !isLegacyTemplate(t));
    if (!target) return false;
    if (view !== "builder") {
      tourOpenedBuilderRef.current = true;
      edit(target);
    }
    return true;
  };
  // Asegura estar en la lista para el paso de "Ejecutar" (se salta si no hay plantillas).
  tourActionsRef.current.runStepForTour = () => {
    if (templates.length === 0) return false;
    if (tourOpenedBuilderRef.current && view !== "list") {
      tourOpenedBuilderRef.current = false;
      setView("list");
      setEditing(null);
    }
    return true;
  };
  // Volver a la lista (al retroceder o terminar) si el tour abrió el builder.
  tourActionsRef.current.backToListForTour = () => {
    if (tourOpenedBuilderRef.current && view !== "list") {
      tourOpenedBuilderRef.current = false;
      setView("list");
      setEditing(null);
    }
    return true;
  };
  tourActionsRef.current.finishTour = () => {
    if (tourOpenedBuilderRef.current) {
      tourOpenedBuilderRef.current = false;
      setView("list");
      setEditing(null);
    }
  };

  const tourSteps = useMemo(() => ([
    {
      title: "Bienvenido a Reportes 👋",
      content: "Aquí creas plantillas de reportes personalizados con gráficos, tablas y KPIs a partir de tus datos. Te muestro lo esencial.",
    },
    {
      target: '[data-tour="new-template"]',
      title: "Nueva plantilla",
      content: "Crea una plantilla de reporte desde cero: eliges la fuente de datos y armas los bloques.",
      placement: "bottom",
    },
    {
      target: '[data-tour="search"]',
      title: "Busca tus plantillas",
      content: "Encuentra cualquier plantilla por su nombre al instante.",
      placement: "bottom",
    },
    {
      target: '[data-tour="template-card"]',
      title: "Tus plantillas",
      content: "Cada tarjeta es una plantilla. Desde los íconos puedes verla, editarla, duplicarla o eliminarla.",
      placement: "auto",
    },
    {
      target: '[data-tour="run-btn"]',
      title: "Ejecutar y ver datos",
      content: "Ejecuta la plantilla para ver los datos, aplicar filtros por fecha y sucursal, y exportar a Excel o PDF.",
      placement: "top",
      action: () => tourActionsRef.current.runStepForTour?.(),
    },
    {
      target: '[data-tour="builder-info"]',
      title: "Constructor de plantillas",
      content: "Ponle un nombre, elige la fuente de datos y describe el propósito de tu reporte.",
      placement: "auto",
      action: () => tourActionsRef.current.openBuilderForTour?.(),
    },
    {
      target: '[data-tour="builder-blocks"]',
      title: "Agrega bloques",
      content: "Añade bloques: gráficos de barras, líneas, circular, tablas o KPIs. Cada bloque consulta tu fuente de datos.",
      placement: "auto",
    },
    {
      target: '[data-tour="builder-block-card"]',
      title: "Configura cada bloque",
      content: "Define el tipo de gráfico, los ejes X/Y, las columnas y el tamaño. Puedes reordenarlos o eliminarlos.",
      placement: "auto",
    },
    {
      target: '[data-tour="builder-save"]',
      title: "Guarda tu plantilla",
      content: "Cuando termines de armar el reporte, guárdalo para poder ejecutarlo cuando quieras.",
      placement: "bottom",
    },
    {
      title: "¡Listo! 🎉",
      content: "Eso es todo. Puedes volver a ver este recorrido cuando quieras desde el botón de ayuda (?) en la barra superior.",
    },
  ]), []);

  return (

    <AppShell>
      <MainContent>
        <div className="rb-root">
          <div className="rb-container">
            {view === "list" && <TemplateList templates={filtered} sources={catalogSources} smartpayCatalog={smartpayCatalog} templatesLoading={templatesLoading} templatesError={templatesError} catalogLoading={catalogLoading} catalogError={catalogError} search={search} setSearch={setSearch} onNew={startNew} onEdit={edit} onDup={dup} onDel={del} onPreview={preview} onHelp={() => setTourSignal(v => v + 1)} />}
            {view === "choose" && <ChooserView onScratch={startFromScratch} onSmartpay={() => setView("smartpay-catalog")} onBack={() => setView("list")} />}
            {view === "smartpay-catalog" && <SmartpayCatalogView catalog={smartpayCatalog} loading={smartpayCatalogLoading} error={smartpayCatalogError} businessCode={businessCode} onBack={() => setView("choose")} onCreated={onSmartpayTemplateCreated} />}
            {view === "builder" && editing && <BuilderView template={editing} sources={catalogSources} catalogLoading={catalogLoading} catalogError={catalogError} saveError={templatesError} onSave={save} onBack={() => { setView("list"); setEditing(null); }} onPreview={preview} />}
            {view === "preview" && previewing && (isLegacyTemplate(previewing)
              ? <LegacyPreviewView template={previewing} catalog={smartpayCatalog} businessCode={businessCode} onBack={() => setView("list")} />
              : <PreviewView template={previewing} sources={catalogSources} businessCode={businessCode} onBack={() => setView(editing ? "builder" : "list")} />)}
          </div>
        </div>

        {/* Onboarding guiado */}
        <GuidedTour
          steps={tourSteps}
          storageKey="reports_onboarding_v1"
          autoStart={!templatesLoading && !catalogLoading && view === "list"}
          startSignal={tourSignal}
          onFinish={() => tourActionsRef.current.finishTour?.()}
        />
      </MainContent>
    </AppShell>

  );
}

/* ═══ TEMPLATE LIST ══════════════════════════════════════════ */
function TemplateList({ templates, sources, smartpayCatalog, templatesLoading, templatesError, catalogLoading, catalogError, search, setSearch, onNew, onEdit, onDup, onDel, onPreview, onHelp }) {
  return <>
    <div className="rb-top-bar">
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}><div className="rb-top-title"><I.Layout /> Reportes personalizados</div></div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button className="rb-btn-primary" data-tour="new-template" onClick={onNew}><I.Plus s={14} /> Nueva plantilla</button>
        <button className="rb-help-btn" title="Ver recorrido guiado" aria-label="Ver recorrido guiado" onClick={onHelp}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
        </button>
      </div>
    </div>
    <div className="rb-search-wrap" data-tour="search" style={{ maxWidth: 400, marginBottom: 20 }}>
      <div className="rb-search-icon"><I.Search /></div>
      <input className="rb-search-input" placeholder="Buscar plantillas..." value={search} onChange={e => setSearch(e.target.value)} />
    </div>
    {templatesLoading && <div className="rb-empty"><div className="rb-empty__title">Cargando plantillas...</div></div>}
    {templatesError && <div className="rb-empty"><div className="rb-empty__title">No se pudieron cargar las plantillas</div><div className="rb-empty__desc">{templatesError}</div></div>}
    {catalogLoading && <div className="rb-empty"><div className="rb-empty__title">Cargando catalogo...</div></div>}
    {catalogError && <div className="rb-empty"><div className="rb-empty__title">No se pudo cargar el catalogo</div><div className="rb-empty__desc">{catalogError}</div></div>}
    {templates.length === 0 ? (
      <div className="rb-empty"><div className="rb-empty__icon"><I.Sparkle /></div><div className="rb-empty__title">Crea tu primer reporte</div><div className="rb-empty__desc">Diseña plantillas personalizadas con datos y gráficos.</div><button className="rb-btn-primary" onClick={onNew}><I.Plus s={14} /> Crear plantilla</button></div>
    ) : (
      <div className="rb-template-grid">
        <button className="rb-new-card" onClick={onNew}><div className="rb-new-card__icon"><I.Plus s={24} /></div><div className="rb-new-card__title">Nueva plantilla</div><div className="rb-new-card__desc">Diseña un reporte desde cero</div></button>
        {templates.map((t, i) => {
          const legacy = isLegacyTemplate(t);
          const src = sources.find(s => s.id === t.source);
          const legacyEntry = legacy ? smartpayCatalog.find(e => e.code === t.legacy_report_code) : null;
          return (
            <div key={t.id} className="rb-tpl-card" data-tour={i === 0 ? "template-card" : undefined} style={{ animationDelay: `${i * 0.05}s` }}>
              <div className="rb-tpl-card__head">
                <div className="rb-tpl-card__info"><span className="rb-tpl-card__emoji">{legacy ? <I.Sparkle /> : (src?.icon || "📊")}</span><div><div className="rb-tpl-card__name">{t.name}</div><div className="rb-tpl-card__meta">{legacy ? `Plantilla SmartPay · ${legacyEntry?.name || t.legacy_report_code}` : `${src?.label} · ${t.blocks.length} bloques`}</div></div></div>
                <div className="rb-tpl-card__actions">
                  <button className="rb-icon-btn" onClick={() => onPreview(t)}><I.Eye /></button>
                  <button className="rb-icon-btn" onClick={() => onEdit(t)}><I.Edit /></button>
                  <button className="rb-icon-btn" onClick={() => onDup(t)}><I.Copy /></button>
                  <button className="rb-icon-btn rb-icon-btn--danger" onClick={() => onDel(t.id)}><I.Trash /></button>
                </div>
              </div>
              <div className="rb-tpl-card__body"><div className="rb-tpl-card__desc">{t.description || "Sin descripción"}</div><div className="rb-tpl-card__blocks">{legacy ? <span className="rb-block-pill">{legacyEntry?.category}</span> : <>{t.blocks.slice(0, 4).map(b => { const ct = chartMeta(chartTypeOf(b)); return <span key={b.id} className="rb-block-pill">{ct?.icon} {b.title}</span>; })}{t.blocks.length > 4 && <span className="rb-block-pill">+{t.blocks.length - 4}</span>}</>}</div></div>
              <div className="rb-tpl-card__foot"><span className="rb-tpl-card__date"><I.Clock />
                {t.updatedAt}</span>
                <button className="rb-btn-secondary" data-tour={i === 0 ? "run-btn" : undefined} style={{ padding: "5px 14px", fontSize: 12 }} onClick={() => onPreview(t)}><I.Play />
                  Ejecutar / Ver datos
                </button></div>
            </div>
          );
        })}
      </div>
    )}
  </>;
}

/* ═══ BUILDER ════════════════════════════════════════════════ */
function BuilderView({ template, sources, catalogLoading, catalogError, saveError, onSave, onBack, onPreview }) {
  const sourceForTemplate = sources.find(s => s.id === template.source);
  const [t, setT] = useState({ ...template, blocks: (template.blocks || []).map(b => normalizeBlock(b, sourceForTemplate)) });
  const set = (k, v) => setT(p => ({ ...p, [k]: v }));
  const src = sources.find(s => s.id === t.source);
  const allowedCharts = getAllowedCharts(src);
  const [adding, setAdding] = useState(false);
  const addBlock = (type) => { if (!src) return; set("blocks", [...t.blocks, createBlock(src, type)]); setAdding(false); };
  const updBlock = (id, u) => set("blocks", t.blocks.map(b => b.id === id ? { ...b, ...u } : b));
  const rmBlock = (id) => set("blocks", t.blocks.filter(b => b.id !== id));
  const mvBlock = (idx, d) => { const bs = [...t.blocks]; const ni = idx + d; if (ni < 0 || ni >= bs.length) return;[bs[idx], bs[ni]] = [bs[ni], bs[idx]]; set("blocks", bs); };
  const valid = t.name.trim() && t.source && t.blocks.length > 0;

  return <>
    <div className="rb-top-bar">
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}><button className="rb-back-btn" onClick={onBack}><I.ArrowLeft /></button><div className="rb-top-title">{template.name ? "Editar plantilla" : "Nueva plantilla"}</div></div>
      <div style={{ display: "flex", gap: 8 }}>{valid && <button className="rb-btn-secondary" onClick={() => onPreview({ ...t, blocks: t.blocks.map(b => normalizeBlock(b, src)) })}><I.Eye /> Vista previa</button>}<button className="rb-btn-primary" data-tour="builder-save" disabled={!valid} onClick={() => valid && onSave({ ...t, blocks: t.blocks.map(b => normalizeBlock(b, src)), updatedAt: toISO() })}><I.Save /> Guardar</button></div>
    </div>
    <div className="rb-section" data-tour="builder-info">
      <div className="rb-section-title">Información</div>
      {saveError && <div style={{ fontSize: 12, color: "#C53030", marginBottom: 10 }}>{saveError}</div>}
      <div className="rb-form-row">
        <div className="rb-fg"><label className="rb-label">Nombre</label><input className="rb-input" placeholder="Ej: Ventas mensuales" value={t.name} onChange={e => set("name", e.target.value)} /></div>
        <div className="rb-fg"><label className="rb-label">Fuente de datos</label><select className="rb-input" value={t.source} disabled={catalogLoading} onChange={e => { set("source", e.target.value); set("blocks", []); setAdding(false); }}><option value="">{catalogLoading ? "Cargando catalogo..." : "Seleccionar..."}</option>{sources.map(s => <option key={s.id} value={s.id}>{s.icon} {s.label}</option>)}</select>{catalogError && <div style={{ fontSize: 12, color: "#C53030", marginTop: 6 }}>{catalogError}</div>}</div>
      </div>
      <div className="rb-fg"><label className="rb-label">Descripción</label><textarea className="rb-input rb-textarea" placeholder="Propósito del reporte..." value={t.description} onChange={e => set("description", e.target.value)} /></div>
    </div>
    {src && <div className="rb-section" data-tour="builder-blocks">
      <div className="rb-section-header"><div className="rb-section-title" style={{ margin: 0 }}>Bloques del reporte</div><button className="rb-btn-secondary" style={{ padding: "6px 14px", fontSize: 12 }} onClick={() => setAdding(true)}><I.Plus s={12} /> Agregar bloque</button></div>
      {adding && <div className="rb-block-picker"><div className="rb-block-picker__title">Tipo de bloque</div><div className="rb-block-picker__grid">{allowedCharts.map(ct => <button key={ct.id} className="rb-type-btn" onClick={() => addBlock(ct.id)}><span className="rb-type-btn__icon">{ct.icon}</span><span className="rb-type-btn__label">{ct.label}</span></button>)}</div>{allowedCharts.length === 0 && <div style={{ fontSize: 12, color: "#9090A0" }}>Esta fuente no tiene tipos de grafico disponibles.</div>}<button className="rb-btn-ghost" style={{ marginTop: 8 }} onClick={() => setAdding(false)}>Cancelar</button></div>}
      {t.blocks.length === 0 && !adding ? <div style={{ textAlign: "center", padding: "40px 20px", color: "#9090A0" }}><div style={{ fontSize: 14, fontWeight: 600, color: "#6B6B80", marginBottom: 4 }}>Sin bloques</div><div style={{ fontSize: 12 }}>Agrega gráficos, tablas o KPIs</div></div> : <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>{t.blocks.map((b, i) => <BlockEditor key={b.id} block={b} source={src} index={i} total={t.blocks.length} dataTour={i === 0 ? "builder-block-card" : undefined} onUpdate={u => updBlock(b.id, u)} onRemove={() => rmBlock(b.id)} onMove={d => mvBlock(i, d)} />)}</div>}
    </div>}
  </>;
}

function BlockEditor({ block, source, index, total, onUpdate, onRemove, onMove, dataTour }) {
  const [exp, setExp] = useState(true);
  const chartType = chartTypeOf(block);
  const config = blockConfig(block);
  const ct = chartMeta(chartType);
  const xFields = getFieldsFor(source, chartType, "x");
  const yFields = getFieldsFor(source, chartType, "y");
  const tableFields = getFieldsFor(source, chartType, "table");
  const sizes = [{ v: "third", l: "⅓" }, { v: "half", l: "½" }, { v: "full", l: "Completo" }];
  const updateConfig = (patch) => onUpdate({ config: { ...config, ...patch }, metricId: patch.y_field || block.metricId });
  return <div className="rb-block-card" data-tour={dataTour}>
    <div className="rb-block-card__head" onClick={() => setExp(!exp)}>
      <div className="rb-block-card__head-left">
        <span className="rb-grip"><I.Grip /></span><span className="rb-accent">{ct?.icon}</span>
        <input className="rb-block-card__title-input" value={block.title} onChange={e => { e.stopPropagation(); onUpdate({ title: e.target.value }); }} onClick={e => e.stopPropagation()} />
      </div>
      <div className="rb-block-card__head-right">
        <span className="rb-block-card__type-badge">{ct?.label}</span>
        {index > 0 && <button className="rb-icon-btn-sm" onClick={e => { e.stopPropagation(); onMove(-1); }}>↑</button>}
        {index < total - 1 && <button className="rb-icon-btn-sm" onClick={e => { e.stopPropagation(); onMove(1); }}>↓</button>}
        <button className="rb-icon-btn-sm rb-icon-btn-sm--danger" onClick={e => { e.stopPropagation(); onRemove(); }}><I.X s={12} /></button>
        <div className={`rb-chev ${exp ? "rb-chev--open" : "rb-chev--closed"}`}><I.ChevDown /></div>
      </div>
    </div>
    {exp && <div className="rb-block-card__body">
      <div className="rb-fg"><label className="rb-label">Tamaño</label><div className="rb-size-group">{sizes.map(s => <button key={s.v} className={`rb-size-btn ${block.size === s.v ? "rb-size-btn--active" : ""}`} onClick={() => onUpdate({ size: s.v })}>{s.l}</button>)}</div></div>
      {isMetricChart(chartType) && <><div className="rb-fg"><label className="rb-label">Eje X</label><select className="rb-input rb-input--sm" value={config.x_field || ""} onChange={e => updateConfig({ x_field: e.target.value })}><option value="">Seleccionar...</option>{xFields.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}</select></div><div className="rb-fg"><label className="rb-label">Eje Y</label><select className="rb-input rb-input--sm" value={config.y_field || ""} onChange={e => updateConfig({ y_field: e.target.value })}><option value="">Seleccionar...</option>{yFields.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}</select></div>{source.groupByOptions.length > 0 && <div className="rb-fg"><label className="rb-label">Agrupar por</label><select className="rb-input rb-input--sm" value={config.group_by || ""} onChange={e => updateConfig({ group_by: e.target.value })}><option value="">Sin agrupacion</option>{source.groupByOptions.map(option => <option key={option} value={option}>{humanize(option)}</option>)}</select></div>}</>}
      {(chartType === "kpi" || chartType === "stat_delta") && <div className="rb-fg"><label className="rb-label">Campo / metrica</label><select className="rb-input rb-input--sm" value={config.y_field || block.metricId || ""} onChange={e => updateConfig({ y_field: e.target.value })}><option value="">Seleccionar...</option>{yFields.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}</select></div>}
      {chartType === "table" && <div className="rb-fg rb-block-card__body--table-cols"><label className="rb-label">Columnas</label><div className="rb-col-pills">{tableFields.map(f => { const sel = (config.columns || []).includes(f.id); return <button key={f.id} className={`rb-col-pill ${sel ? "rb-col-pill--active" : ""}`} onClick={() => updateConfig({ columns: sel ? (config.columns || []).filter(x => x !== f.id) : [...(config.columns || []), f.id] })}>{f.label}</button>; })}</div></div>}
    </div>}
  </div>;
}

/* ═══ PREVIEW ════════════════════════════════════════════════ */
function PreviewView({ template, sources, businessCode, onBack }) {
  const src = sources.find(s => s.id === template.source);
  const blocks = useMemo(() => (template.blocks || []).map(b => normalizeBlock(b, src)), [template.blocks, src]);
  const fdefs = src?.filters || [];
  const userBranches = useMemo(() => getUserBranches(), []);
  const [selectedBranchCodes, setSelectedBranchCodes] = useState(() => getUserBranches().map(branch => branch.code));
  const [dFrom, setDFrom] = useState(""); const [dTo, setDTo] = useState(""); const [qr, setQr] = useState(null); const [filters, setFilters] = useState({});
  const [metricData, setMetricData] = useState({}); const [metricError, setMetricError] = useState("");
  const effectiveBranchCodes = useMemo(
    () => selectedBranchCodes.length ? selectedBranchCodes : userBranches.map(branch => branch.code),
    [selectedBranchCodes, userBranches]
  );
  const toggleBranch = (code) => {
    setSelectedBranchCodes((prev) => {
      if (!prev.includes(code)) return [...prev, code];
      return prev.length > 1 ? prev.filter(item => item !== code) : prev;
    });
  };
  const selectAllBranches = () => setSelectedBranchCodes(userBranches.map(branch => branch.code));
  const hasFilters = dFrom || dTo || Object.values(filters).some(v => v && v !== "Todas" && v !== "Todos");
  const resolveFilters = useMemo(() => {
    const next = { date_range: dFrom || dTo ? "custom" : "month" };
    if (dFrom) next.date_from = dFrom;
    if (dTo) next.date_to = dTo;
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "Todas" && value !== "Todos") next[key] = value;
    });
    return next;
  }, [dFrom, dTo, filters]);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exporting, setExporting] = useState("");

  const buildCurrentExportPayload = useCallback(() => buildReportExportPayload({
    template,
    businessCode,
    blocks,
    filters: resolveFilters,
    branchCodes: effectiveBranchCodes,
    branches: userBranches,
    metricData,
    source: src,
  }), [template, businessCode, blocks, resolveFilters, effectiveBranchCodes, userBranches, metricData, src]);

  const handleExportExcel = async () => {
    setExporting("excel");
    try {
      const response = await ReportTemplatesAPI.exportExcel(buildCurrentExportPayload(), getToken());
      const headerName = getFilenameFromDisposition(response.headers?.["content-disposition"]);
      const filename = headerName || `${safeFileName(template.name)}.xlsx`;
      downloadBlob(response.data, filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`);
      setExportModalOpen(false);
    } catch (error) {
      showExportToast(getErrorMessage(error, "No se pudo exportar Excel"));
    } finally {
      setExporting("");
    }
  };

  const handleExportPdf = () => {
    setExporting("pdf");
    try {
      const docDefinition = buildReportPdfDefinition({
        template,
        filters: resolveFilters,
        branchLabel: selectedBranchNames(userBranches, effectiveBranchCodes),
        tables: getExportTables(blocks, metricData, src),
        summaryRows: buildExportSummary(blocks, metricData),
      });
      pdfMake.createPdf(docDefinition).download(`${safeFileName(template.name)}.pdf`);
      setExportModalOpen(false);
    } catch (error) {
      showExportToast(getErrorMessage(error, "No se pudo exportar PDF"));
    } finally {
      setExporting("");
    }
  };

  useEffect(() => {
    let alive = true;
    const resolveMetrics = async () => {
      if (!businessCode || blocks.length === 0) {
        setMetricData({});
        return;
      }
      setMetricError("");
      setMetricData(blocks.reduce((acc, block) => ({ ...acc, [block.id]: { loading: true } }), {}));
      try {
        const response = await DashboardMetricsAPI.resolveDashboardMetrics({
          business_code: businessCode,
          branchoffice_code: effectiveBranchCodes,
          filters: resolveFilters,
          widgets: blocks.map(toMetricWidget),
        }, getToken());
        if (!alive) return;
        setMetricData(normalizeResolveResults(response, blocks));
      } catch (error) {
        if (!alive) return;
        const message = error?.response?.data?.message || error?.message || "No se pudieron resolver las metricas";
        setMetricError(message);
        setMetricData(blocks.reduce((acc, block) => ({ ...acc, [block.id]: { loading: false, error: message } }), {}));
      }
    };
    resolveMetrics();
    return () => { alive = false; };
  }, [businessCode, blocks, effectiveBranchCodes, resolveFilters]);

  const clearAll = () => { setDFrom(""); setDTo(""); setQr(null); setFilters({}); };

  return <>
    <div className="rb-top-bar">
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}><button className="rb-back-btn" onClick={onBack}><I.ArrowLeft /></button><div><div className="rb-top-title">{template.name || "Vista previa"}</div><div style={{ fontSize: 12, color: "#9090A0" }}>{src?.icon} {src?.label} Â· {blocks.length} bloques</div></div></div>
      <button className="rb-btn-secondary" onClick={() => setExportModalOpen(true)}><I.Download /> Exportar</button>
    </div>

    <div className="rb-filter-bar">
      <div className="rb-filter-bar__top">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}><div className="rb-filter-bar__icon"><I.Calendar /></div><span className="rb-filter-bar__title">Periodo</span></div>
        <div className="rb-filter-bar__quick">{QUICK.map((q, i) => <button key={i} className={`rb-quick-btn ${qr === i ? "rb-quick-btn--active" : ""}`} onClick={() => { setDFrom(q.from()); setDTo(q.to()); setQr(i); }}>{q.label}</button>)}</div>
        {hasFilters && <button className="rb-clear-btn" onClick={clearAll}><I.Refresh /> Limpiar</button>}
      </div>
      <div className="rb-filter-bar__grid">
        {userBranches.length > 0 && <div className="rb-fg"><label className="rb-label">Sucursales</label><details className="rb-branch-menu"><summary>{branchSummary(userBranches, selectedBranchCodes)}</summary><div className="rb-branch-panel"><button type="button" className="rb-branch-all" onClick={selectAllBranches}>Todas</button>{userBranches.map(branch => <label key={branch.code} className="rb-branch-option"><input type="checkbox" checked={effectiveBranchCodes.includes(branch.code)} onChange={() => toggleBranch(branch.code)} /><span>{branch.name}</span></label>)}</div></details></div>}
        <div className="rb-fg"><label className="rb-label">Desde</label><input type="date" className="rb-input" value={dFrom} onChange={e => { setDFrom(e.target.value); setQr(null); }} /></div>
        <div className="rb-fg"><label className="rb-label">Hasta</label><input type="date" className="rb-input" value={dTo} onChange={e => { setDTo(e.target.value); setQr(null); }} /></div>
        {fdefs.map(f => <div key={f.key} className="rb-fg"><label className="rb-label">{f.label}</label><select className="rb-input" value={filters[f.key] || f.options[0] || ""} onChange={e => { setFilters(p => ({ ...p, [f.key]: e.target.value })); }}>{f.options.map(o => <option key={o}>{o}</option>)}</select></div>)}
      </div>
      {hasFilters && <div className="rb-active-pills">
        {dFrom && <span className="rb-pill"><I.Calendar /> {fmtD(dFrom)}<button className="rb-pill__x" onClick={() => { setDFrom(""); setQr(null); }}><I.X s={10} /></button></span>}
        {dTo && <span className="rb-pill"><I.Calendar /> {fmtD(dTo)}<button className="rb-pill__x" onClick={() => { setDTo(""); setQr(null); }}><I.X s={10} /></button></span>}
        {Object.entries(filters).filter(([, v]) => v && v !== "Todas" && v !== "Todos").map(([k, v]) => <span key={k} className="rb-pill"><I.Filter /> {v}<button className="rb-pill__x" onClick={() => setFilters(p => ({ ...p, [k]: "" }))}><I.X s={10} /></button></span>)}
      </div>}
      {metricError && <div style={{ fontSize: 12, color: "#C53030", marginTop: 10 }}>{metricError}</div>}
    </div>

    <div className="rb-preview-grid">
      {blocks.map(block => (
        <div key={block.id} className={`rb-preview-block ${block.size === "full" ? "rb-preview-block--full" : ""}`}>
          <div className="rb-preview-block__head">{block.title}</div>
          <div className={`rb-preview-block__body ${["kpi", "stat_delta"].includes(chartTypeOf(block)) ? "rb-preview-block__body--kpi" : "rb-preview-block__body--chart"}`}>
            <RenderBlock block={block} result={metricData[block.id]} source={src} />
          </div>
        </div>
      ))}
    </div>

    <ExportModal
      open={exportModalOpen}
      exporting={exporting}
      onClose={() => !exporting && setExportModalOpen(false)}
      onExcel={handleExportExcel}
      onPdf={handleExportPdf}
    />
  </>;
}

export function ExportModal({ open, exporting, onClose, onExcel, onPdf }) {
  if (!open) return null;
  const loading = Boolean(exporting);
  return <div className="rb-export-backdrop" role="presentation" onMouseDown={onClose}>
    <div className="rb-export-modal" role="dialog" aria-modal="true" aria-labelledby="rb-export-title" onMouseDown={event => event.stopPropagation()}>
      <div className="rb-export-modal__head">
        <div>
          <div id="rb-export-title" className="rb-export-modal__title">Exportar reporte</div>
          <div className="rb-export-modal__desc">Selecciona el formato de descarga.</div>
        </div>
        <button className="rb-icon-btn" disabled={loading} onClick={onClose}><I.X s={14} /></button>
      </div>
      <div className="rb-export-modal__actions">
        {onExcel && <button className="rb-export-option" disabled={loading} onClick={onExcel}>
          <I.Download />
          <span>{exporting === "excel" ? "Exportando Excel..." : "Exportar Excel"}</span>
        </button>}
        {onPdf && <button className="rb-export-option" disabled={loading} onClick={onPdf}>
          <I.Download />
          <span>{exporting === "pdf" ? "Generando PDF..." : "Exportar PDF"}</span>
        </button>}
      </div>
      {loading && <div className="rb-export-modal__loading">Preparando archivo...</div>}
    </div>
  </div>;
}

function formatFieldValue(value, field) {
  if (field?.type === "currency") return fmt(Number(value) || 0);
  if (field?.type === "percentage") return `${fmtMetric(value)}%`;
  if (isNumericField(field)) return fmtMetric(value);
  return value ?? "";
}

function getField(source, fieldId) {
  return source?.fields?.find(f => f.id === fieldId) || source?.tableFields?.find(f => f.id === fieldId) || source?.yFields?.find(f => f.id === fieldId) || null;
}

function BlockState({ title, detail }) {
  return <div style={{ height: "100%", minHeight: 120, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", color: "#9090A0", gap: 4 }}><div style={{ fontSize: 13, fontWeight: 700, color: "#6B6B80" }}>{title}</div>{detail && <div style={{ fontSize: 12 }}>{detail}</div>}</div>;
}

function normalizeChartRows(payload, block) {
  const config = blockConfig(block);
  return extractRows(payload).map((row, index) => {
    const label = row?.label ?? row?.name ?? row?.[config.x_field] ?? row?.period ?? row?.date ?? `Item ${index + 1}`;
    const rawValue = row?.value ?? row?.[config.y_field] ?? row?.total ?? row?.amount ?? row?.count ?? 0;
    return { name: String(label).substring(0, 18), value: Number(rawValue) || 0 };
  }).filter(row => row.name && Number.isFinite(row.value));
}

function RenderBlock({ block, result, source }) {
  const chartType = chartTypeOf(block);
  const config = blockConfig(block);
  const payload = result?.payload;

  if (result?.loading) return <BlockState title="Cargando metrica..." />;
  if (result?.error) return <BlockState title="Metrica no disponible" detail={result.error} />;
  if (result?.noData || !payload) return <BlockState title="Sin informacion" detail="No hay informacion para este filtro" />;

  if (chartType === "kpi" || chartType === "stat_delta") {
    const field = getField(source, config.y_field || block.metricId);
    const value = payload.formatted_value || formatFieldValue(payload.value, field);
    return <div className="rb-kpi-value">{value}</div>;
  }

  if (chartType === "table") {
    const fields = (config.columns || []).map(fid => getField(source, fid)).filter(Boolean);
    const rows = extractRows(payload);
    if (!rows.length || !fields.length) return <BlockState title="Sin informacion" detail="No hay informacion para este filtro" />;
    return <TableBlockRenderer rows={rows} fields={fields} />;
  }

  const cd = normalizeChartRows(payload, block);
  if (!cd.length) return <BlockState title="Sin informacion" detail="No hay informacion para este filtro" />;
  const tooltip = <Tooltip formatter={(value) => fmtMetric(value)} />;
  if (chartType === "bar" || chartType === "bar_h") return <ResponsiveContainer width="100%" height={200}><BarChart data={cd} layout={chartType === "bar_h" ? "vertical" : "horizontal"}><CartesianGrid strokeDasharray="3 3" stroke="#F0F0F4" />{chartType === "bar_h" ? <><XAxis type="number" tick={{ fontSize: 10 }} /><YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} /></> : <><XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /></>}{tooltip}<Bar dataKey="value" fill="#5B5BD6" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer>;
  if (chartType === "line") return <ResponsiveContainer width="100%" height={200}><LineChart data={cd}><CartesianGrid strokeDasharray="3 3" stroke="#F0F0F4" /><XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} />{tooltip}<Line type="monotone" dataKey="value" stroke="#5B5BD6" strokeWidth={2} dot={{ fill: "#5B5BD6", r: 3 }} /></LineChart></ResponsiveContainer>;
  if (chartType === "area") return <ResponsiveContainer width="100%" height={200}><AreaChart data={cd}><CartesianGrid strokeDasharray="3 3" stroke="#F0F0F4" /><XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} />{tooltip}<Area type="monotone" dataKey="value" stroke="#5B5BD6" fill="#5B5BD620" strokeWidth={2} /></AreaChart></ResponsiveContainer>;
  if (chartType === "pie") return <ResponsiveContainer width="100%" height={200}><PieChart><Pie data={cd} cx="50%" cy="50%" outerRadius={75} dataKey="value" label={({ name }) => name}>{cd.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}</Pie>{tooltip}</PieChart></ResponsiveContainer>;
  return <BlockState title="Tipo no soportado" detail={chartType} />;
}

function TableBlockRenderer({ rows, fields }) {
  const [search, setSearch] = useState("");
  const [rpp, setRpp] = useState(10);
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let nextRows = q
      ? rows.filter(row => fields.some(field => String(row[field.id] ?? row[field.key] ?? "").toLowerCase().includes(q)))
      : [...rows];

    if (sortKey) {
      nextRows.sort((a, b) => {
        const av = a[sortKey];
        const bv = b[sortKey];
        const numeric = typeof av === "number" || typeof bv === "number";
        if (numeric) return sortDir === "asc" ? (Number(av) || 0) - (Number(bv) || 0) : (Number(bv) || 0) - (Number(av) || 0);
        return sortDir === "asc"
          ? String(av ?? "").localeCompare(String(bv ?? ""))
          : String(bv ?? "").localeCompare(String(av ?? ""));
      });
    }

    return nextRows;
  }, [rows, fields, search, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rpp));
  const safePage = Math.min(page, totalPages);
  const pagedRows = filteredRows.slice((safePage - 1) * rpp, safePage * rpp);
  const pageNums = () => {
    const pages = [];
    const max = 5;
    let start = Math.max(1, safePage - Math.floor(max / 2));
    let end = Math.min(totalPages, start + max - 1);
    if (end - start < max - 1) start = Math.max(1, end - max + 1);
    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push("...");
    }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };
  const sort = (key) => {
    if (sortKey === key) setSortDir(dir => dir === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(1);
  };

  return <div className="rb-table-section rb-table-section--block">
    <div className="rb-table-toolbar">
      <div className="rb-search-wrap" style={{ flex: 1, maxWidth: 360 }}>
        <div className="rb-search-icon"><I.Search s={14} /></div>
        <input className="rb-search-input" placeholder="Buscar en resultados..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}><span className="rb-rpp-label">Filas</span><div className="rb-rpp-group">{[5, 10, 20, 50].map(n => <button key={n} className={`rb-rpp-btn ${rpp === n ? "rb-rpp-btn--active" : ""}`} onClick={() => { setRpp(n); setPage(1); }}>{n}</button>)}</div></div>
    </div>
    <div className="rb-table-wrap">
      <table className="rb-table">
        <thead><tr>{fields.map(field => (
          <th key={field.id} style={{ textAlign: isNumericField(field) ? "right" : "left" }} onClick={() => sort(field.id)}>
            <div className={`rb-th-content ${isNumericField(field) ? "rb-th-content--right" : ""}`}>{field.label}{sortKey === field.id ? (sortDir === "asc" ? <I.SortAsc /> : <I.SortDesc />) : <span style={{ width: 11 }} />}</div>
          </th>
        ))}</tr></thead>
        <tbody>
          {pagedRows.length === 0 ? <tr><td colSpan={fields.length}><div className="rb-table-empty"><div className="rb-table-empty__title">Sin resultados</div><div className="rb-table-empty__desc">Ajusta la busqueda</div></div></td></tr>
            : pagedRows.map((row, ri) => (
              <tr key={row.id || row._id || ri}>{fields.map(field => <td key={field.id} style={{ textAlign: isNumericField(field) ? "right" : "left" }}>{formatFieldValue(row[field.id] ?? row[field.key], field)}</td>)}</tr>
            ))}
        </tbody>
      </table>
    </div>
    <div className="rb-pagination">
      <div className="rb-pagination__info">{filteredRows.length === 0 ? "0 resultados" : <>Mostrando <b>{(safePage - 1) * rpp + 1}</b>-<b>{Math.min(safePage * rpp, filteredRows.length)}</b> de <b>{filteredRows.length}</b></>}</div>
      <div className="rb-pagination__pages">
        <button className="rb-page-btn" disabled={safePage <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}><I.ChevLeft /></button>
        {pageNums().map((p, i) => p === "..." ? <span key={`e${i}`} className="rb-pagination__ellipsis">...</span> : <button key={p} className={`rb-page-btn ${safePage === p ? "rb-page-btn--active" : ""}`} onClick={() => setPage(p)}>{p}</button>)}
        <button className="rb-page-btn" disabled={safePage >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}><I.ChevRight /></button>
      </div>
    </div>
  </div>;
}